import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowRight, ArrowLeft, KeyRound } from 'lucide-react';
import { API } from '../lib/constants';

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const Verify = () => {
  const navigate = useNavigate();
  const qpEmail = new URLSearchParams(window.location.search).get('email') || '';
  const [email, setEmail] = useState(qpEmail || localStorage.getItem('userEmail') || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // email may be URL-encoded
  useEffect(() => {
    if (qpEmail) {
      try { setEmail(decodeURIComponent(qpEmail)); } catch { setEmail(qpEmail); }
    }
  }, [qpEmail]);

  const requestCode = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter the email on your account'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/auth/request-code`, { email }, { withCredentials: true });
      toast.success(`If an account exists, a 6-digit code was sent to ${email}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter the email on your account'); return; }
    if (!/^\d{6}$/.test(code)) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/verify-code`, { email, code }, { withCredentials: true });
      if (res.data.success) {
        localStorage.setItem('userId', res.data.user_id);
        localStorage.setItem('userEmail', res.data.email);
        if (res.data.username) localStorage.setItem('username', res.data.username);
        if (res.data.is_admin) localStorage.setItem('isAdmin', 'true');
        toast.success("You're verified — your profile is now live!");
        navigate(res.data.is_admin && !res.data.username ? '/admin' : '/dashboard');
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/offersz-lockup.png" alt="Offersz" data-testid="verify-wordmark" className="w-40 h-auto mx-auto mb-6" />
          <h1 className="text-3xl font-black mb-2">Verify your account</h1>
          <p className="text-white/50">Enter the 6-digit code we sent to your email or that an admin DM'd you on X.</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
              <Mail className="h-4 w-4" /> Email on your account
            </label>
            <Input
              value={email}
              onChange={e => setEmail(e.target.value)}
              type="email"
              placeholder="you@email.com"
              data-testid="verify-page-email-input"
              className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> 6-digit code
            </label>
            <Input
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              data-testid="verify-page-code-input"
              onKeyPress={e => e.key === 'Enter' && verify()}
              className="bg-white/5 border-white/10 text-white h-14 rounded-xl text-center text-3xl tracking-[0.5em] font-bold"
            />
          </div>

          <Button
            onClick={verify}
            disabled={!email || code.length !== 6 || loading}
            data-testid="verify-page-submit-btn"
            className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg"
          >
            {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ArrowRight className="mr-2 h-5 w-5" />}
            Verify & go live
          </Button>

          <button onClick={requestCode} disabled={sending} data-testid="verify-page-resend-btn" className="w-full text-white/40 hover:text-white/70 text-sm py-1">
            {sending ? 'Sending…' : 'Email me a new code'}
          </button>

          <button onClick={() => navigate('/dashboard')} className="w-full text-white/20 text-sm py-2 hover:text-white/40 flex items-center justify-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default Verify;
